/**
 * LabelGrid — the three marks that make every marketplace card the same card.
 *
 * The raise this file implements is "label grid": a product card is a SHIPPING
 * LABEL, not a tile. Name, measure and price are the same three rules on every
 * card, drawn at the same y-offsets, so a wall of twelve reads as one system
 * instead of twelve independent compositions. Three primitives are all that is
 * needed to hold that:
 *
 *   <PriceFigure>   the hero. The figure is `.g-data` (IBM Plex Mono, tabular,
 *                   dotted zero) in `--g-figure` ink at real weight; the unit
 *                   — **сом**, the product's own spelling, never "KGS" — sits
 *                   beside it in body type at meta weight. Tabular figures are
 *                   the whole point: 29 000 and 5 000 line up digit-for-digit
 *                   down a column, which is what lets a buyer compare prices by
 *                   scanning rather than by reading.
 *
 *   <StockMark>     availability as META. A 6px dot (solid = stocked, hollow =
 *                   absent) plus a text label in contrast-safe ink. See
 *                   `stock-pill.ts` for why this replaced the filled pill.
 *
 *   <VerifiedMark>  the quietest thing on the card. Every product on this
 *                   public surface comes from a verified seller (the search and
 *                   detail queries filter `verification_status='verified'`), so
 *                   the badge is TRUE for all 421 of them — which is exactly
 *                   why it must not be a second green pill competing with
 *                   availability under the price. A hairline check plus the
 *                   localized phrase in `--g-ink-2` says it once and gets out
 *                   of the way. The phrase stays visible (not `sr-only`): the
 *                   audience is a first-time renovator whose controlling fear
 *                   is being cheated, and a bare glyph does not answer that.
 *
 * All three are shared by the browse card, the search card and the detail page
 * so those surfaces cannot drift apart. Tokens via the `--g-*` mirror; strings
 * from the `marketplace` namespace. No PII renders here.
 */
import { Check } from "lucide-react";
import { type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { iconProps } from "@/lib/icons";
import { kgsLocaleForLanguage } from "@/lib/format-currency";
import {
  formatPriceFigure,
  formatPriceRange,
} from "@/features/marketplace/price-format";
import {
  STOCK_MARK,
  normalizeStockState,
} from "@/features/marketplace/stock-pill";

export interface PriceFigureProps {
  /** Decimal сом (NOT tiyin). `null` renders the "price on request" fallback. */
  price: number | null;
  /**
   * The HIGH end of a price band, when the product sells in one (v0052).
   * `price` is then the low end, and rendering it alone quotes 5 500 for a
   * product that sells at 5 500–14 000 — which half the catalogue does.
   */
  priceMax?: number | null;
  /** `tiered` marks a vendor LADDER: real ends, pack-size rungs between. */
  priceKind?: string | null;
  /** Figure scale. `card` is the grid hero; `detail` is the page hero. */
  size?: "card" | "detail";
  className?: string;
}

/** The hero figure of any marketplace surface: mono, tabular, unit beside it. */
export function PriceFigure({
  price,
  priceMax = null,
  priceKind = null,
  size = "card",
  className,
}: PriceFigureProps): ReactElement {
  const { t, i18n } = useTranslation("marketplace");
  const locale = kgsLocaleForLanguage(i18n.language);
  // A band only where the high end is genuinely above the low one — equal ends
  // are an exact price written twice, and "5 500 – 5 500" is noise.
  const isRange = price !== null && priceMax !== null && priceMax > price;
  const isTiered = isRange && priceKind === "tiered";

  // "Price on request" is PROSE, not a reading — it never takes the mono face.
  if (price === null) {
    return (
      <span
        className={[
          "font-medium text-[color:var(--g-ink-2)]",
          size === "detail" ? "text-base" : "text-sm",
          className ?? "",
        ].join(" ")}
      >
        {t("card.noPrice")}
      </span>
    );
  }

  const figure = (
    <>
      <span
        className={[
          "g-data font-bold leading-none text-[color:var(--g-figure)]",
          size === "detail" ? "text-3xl" : "text-xl",
        ].join(" ")}
      >
        {isRange
          ? formatPriceRange(price, priceMax as number, locale)
          : formatPriceFigure(price, locale)}
      </span>
      <span
        className={[
          "font-medium leading-none text-[color:var(--g-ink-2)]",
          size === "detail" ? "text-sm" : "text-xs",
        ].join(" ")}
      >
        {t("card.currency")}
      </span>
    </>
  );

  if (!isTiered) {
    return (
      <span className={["flex items-baseline gap-1.5", className ?? ""].join(" ")}>
        {figure}
      </span>
    );
  }

  // The ladder caveat. Said once, under the figure, in prose type — the band's
  // ends are real but the values between them are pack-size rungs, so the
  // figure alone would imply a continuum the seller never offered.
  return (
    <span className={["flex flex-col gap-0.5", className ?? ""].join(" ")}>
      <span className="flex items-baseline gap-1.5">{figure}</span>
      <span
        className={[
          "font-medium text-[color:var(--g-ink-2)]",
          size === "detail" ? "text-xs" : "text-[11px]",
        ].join(" ")}
      >
        {t("card.priceTiered")}
      </span>
    </span>
  );
}

export interface StockMarkProps {
  /** Raw server stock string; coerced to a known state. */
  state: string;
  /** Test hook the browse grid and the detail page already assert on. */
  testId?: string;
  /** `detail` adds a hairline chip frame so it holds its own beside the hero. */
  variant?: "meta" | "chip";
}

/** Availability, said once and quietly: dot (shape + colour) plus a label. */
export function StockMark({
  state,
  testId,
  variant = "meta",
}: StockMarkProps): ReactElement {
  const { t } = useTranslation("marketplace");
  const normalized = normalizeStockState(state);
  const label = t(`stock.${normalized}`);
  const style = STOCK_MARK[normalized];

  return (
    <span
      data-testid={testId}
      aria-label={t("aria.stockState", { state: label })}
      className={[
        "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap text-xs font-medium",
        variant === "chip"
          ? "rounded-[color:var(--g-radius-pill)] rounded-full border border-[color:var(--g-rule)] px-2.5 py-1"
          : "",
        style.label,
      ].join(" ")}
    >
      <span
        aria-hidden="true"
        className={`size-1.5 shrink-0 rounded-full ${style.dot}`}
      />
      {label}
    </span>
  );
}

export interface VerifiedMarkProps {
  /** Test hook the browse card asserts on. */
  testId?: string;
  className?: string;
}

/** "Verified seller", stated once in meta ink — never a competing badge. */
export function VerifiedMark({
  testId,
  className,
}: VerifiedMarkProps): ReactElement {
  const { t } = useTranslation("marketplace");
  return (
    <span
      data-testid={testId}
      className={[
        "inline-flex min-w-0 items-center gap-1 text-[0.6875rem] font-medium text-[color:var(--g-ink-2)]",
        className ?? "",
      ].join(" ")}
    >
      <Check
        {...iconProps("xs")}
        className="shrink-0 text-[color:var(--g-primary)]"
      />
      <span className="truncate">{t("trust.verifiedSeller")}</span>
    </span>
  );
}

/**
 * The seller's BRAND name, with a trailing parenthetical dropped when — and
 * only when — that parenthetical provably names the same stall the card
 * already prints on its own rule.
 *
 * The real catalogue stores business names like
 * `Dinqi (контейнер Б37-39, рынок Уста)`, so a card that also renders
 * `УСТА · Б37-39` said the location twice, once truncated mid-word. The guard
 * is deliberately narrow: the parenthetical must CONTAIN the product's own
 * pavilion number, which is a fact from the same row rather than a guess about
 * naming conventions. `ТЕБО (склад №2)` keeps its parenthetical, because
 * nothing on the card claims that stall. Nothing is ever invented or
 * abbreviated — the full name still reaches AT through the link's aria-label
 * and is printed in full on the product-detail page.
 */
export function sellerBrandName(
  businessName: string,
  pavilionNumber: string,
): string {
  if (pavilionNumber.trim().length === 0) return businessName;
  const match = /^(.*?)\s*\(([^()]*)\)\s*$/.exec(businessName);
  if (match === null) return businessName;
  const [, head = "", inside = ""] = match;
  if (!inside.includes(pavilionNumber)) return businessName;
  return head.trim().length > 0 ? head.trim() : businessName;
}

export interface StallLineProps {
  market: string;
  pavilion: string;
  className?: string;
}

/**
 * Where to physically walk: the market as a proper noun, the stall code as
 * DATA (`.g-data` — a container number is a reading, not prose). This is the
 * "measure" rule of the label grid, and per the product principles it is half
 * the payload of any answer.
 */
export function StallLine({
  market,
  pavilion,
  className,
}: StallLineProps): ReactElement {
  const { t } = useTranslation("marketplace");
  return (
    <span
      aria-label={t("aria.place", { market, pavilion })}
      className={[
        "flex min-w-0 items-center gap-1.5 text-[0.6875rem] uppercase tracking-[0.08em] text-[color:var(--g-ink-2)]",
        className ?? "",
      ].join(" ")}
    >
      <span className="truncate">{market}</span>
      <span aria-hidden="true" className="text-[color:var(--g-rule-strong)]">
        ·
      </span>
      <span className="g-data shrink-0 normal-case tracking-normal">
        {pavilion}
      </span>
    </span>
  );
}
